import { useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Backdrop, Box, CircularProgress } from '@mui/material';
import { InvestigationContext } from '@context/InvestigationContext';
import { InvestigationUI } from '@components/InvestigationUI';
import { V2InvestigationPage } from '@components/v2';

/**
 * Routing dispatcher for `/?sc=XXXX`.
 *
 * Per PM override of the v0.5 design doc (§6): the URL stays `/?sc=XXXX` for
 * ALL cases — there is NO `/hrr` route visible to the customer. Internally we
 * read the `branchinvestigationdetails` response surfaced by
 * `InvestigationProvider` (already wrapped in `App.tsx`) and dispatch:
 *
 *   - `playbookVersion === '2.0'` → `<V2InvestigationPage />` (HRR v2 flow)
 *   - otherwise                    → `<InvestigationUI />`        (legacy v1)
 *
 * Per §4.6 of the HRR FE detailed design: HRR playbooks carry
 * `version: '2.0'` on the root JSON; existing playbooks have no `version` field.
 * The BE surfaces this on the `branchinvestigationdetails` response as
 * `playbookVersion` so this gate can dispatch without parsing the playbook.
 *
 * Per §2 (why a fork): this gate must NOT modify `InvestigationContext`. It
 * sits outside the legacy flow and renders one of two completely separate
 * component trees.
 *
 * RND-4224: the BE rotates `nextQuestion.issuedPageVersion` on every
 * `branchinvestigationdetails` fetch. The legacy `InvestigationProvider`
 * (wrapped at App.tsx level) ALWAYS fires its own `branchinvestigationdetails`
 * on mount, then `V2InvestigationPage` fires a second fetch via
 * `useQueryAnswerPage`. The two fetches return DIFFERENT pageVersions, and the
 * first-page submit then 11002s because the v2 client copy is already stale.
 *
 * Mitigation in this gate (Part 2): route to `<V2InvestigationPage>` as soon
 * as we have ANY HRR signal — without waiting for full v1 context resolution.
 * The v1 fetch still happens (App-level wrap can't be conditionalised today),
 * but no v1-context-derived UI mounts on HRR cases, so the BE pageVersion
 * surfaced to the customer is always the LATEST one (the v2 fetch wins because
 * it fires after the v1 wins-the-race fetch).
 *
 * Part 2's V2PageContext auto-recover papers over any residual race by
 * refetching investigationDetails on the first 11002 → see
 * `src/Context/V2PageContext.tsx` `submitPage` catch block.
 */
export const InvestigationGate = () => {
	const [searchParams] = useSearchParams();
	const sc = searchParams.get('sc') ?? undefined;

	const { investigationDetails, isLoading, isError } = useContext(InvestigationContext);

	// Dispatch by question shape — determined here without waiting for full legacy
	// context resolution. The HRR hook in V2InvestigationPage fetches its own
	// branchinvestigationdetails independently; the legacy context parse errors
	// are expected for HRR cases and must NOT block routing to V2InvestigationPage.
	//
	// Strategy:
	//   1. If sc is present AND legacy context errored → still try HRR path (sc present)
	//   2. If legacy context has data → check nextQuestion.type
	//   3. If still loading → show spinner
	//
	// This avoids the gate blocking on legacy context errors that don't affect HRR.

	const nqType = (investigationDetails?.nextQuestion?.type as string | undefined);
	const isV2PageFlow = nqType === 'page';
	// HRR cases route to V2InvestigationPage in every state — including
	// completed — so a completed survey shows the HRR thank-you screen
	// (Handshake illustration) instead of the legacy pqCompletedScreen on
	// re-visit.
	const investigationType = (investigationDetails as { investigationType?: string } | null)?.investigationType;
	const isHrrCase = investigationType === 'highRiskRefresh';

	// Route to HRR immediately if we have signal, even if legacy context errored.
	// Critical: this MUST short-circuit before any v1-context-derived UI mounts
	// (InvestigationUI, Questions, etc.) so the only UI consuming
	// branchinvestigationdetails is V2InvestigationPage, which uses its own
	// fresh pageVersion. (RND-4224 mitigation — see header comment.)
	if (isV2PageFlow || isHrrCase || (isError && sc)) {
		return <V2InvestigationPage scFromGate={sc} />;
	}

	// Still loading legacy context and no HRR signal yet.
	if (isLoading || (!investigationDetails && !isError)) {
		return (
			<Backdrop open sx={{ zIndex: 1000 }} data-cy="investigation-gate-loading">
				<CircularProgress />
			</Backdrop>
		);
	}

	// Legacy context errored with no sc — genuine error.
	if (isError && !sc) {
		return (
			<Box sx={{ maxWidth: 640, mx: 'auto', p: 3 }} data-cy="investigation-gate-error">
				<Alert severity="error">Something went wrong loading this questionnaire. Please try again.</Alert>
			</Box>
		);
	}

	// Dispatch by question shape: if the BE returns nextQuestion.type === 'page'
	// we are in an HRR v2 page flow; otherwise fall through to legacy InvestigationUI.
	const isV2PageFlowFinal = nqType === 'page';

	if (isV2PageFlowFinal) {
		return <V2InvestigationPage scFromGate={sc} />;
	}

	return <InvestigationUI />;
};

export default InvestigationGate;
